import React, { useState } from 'react';

import { FullCycleRenderWithProfiler } from '@confluence/ssr-utilities/entry-points/FullCycleRender.ts';
import type { NativeCollabQueryResponse } from '@confluence/native-collab/entry-points/NativeCollabQuery.ts';
import { NativeCollabPresenceStateContainer } from '@confluence/native-collab/entry-points/useNativeCollabPresenceState.ts';
import { NativeCollabQuery } from '@confluence/native-collab/entry-points/NativeCollabQuery.graphql.ts';
import { useIsLivePageWithSSR } from '@confluence/live-pages-utils/entry-points/useIsLivePageWithSSR.ts';
import { VIEW_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import { Redirection } from '@confluence/route-manager/entry-points/Redirection.ts';

import { TreeRootForServerPlaceholderWrapper } from '../TreeRootForServerPlaceholderWrapper.tsx';
import { FullPageEditorLoader } from '../FullPageEditorLoader.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

type EditPageLoaderServerProps = {
	contentId: string;
	spaceKey: string;
	draftShareId?: string;
	contentType: string;
};

// This component is supposed to be server-side version of EditPageLoader and will be used
// for rendering content area / breadcrumbs and possibly editor toolbar, i.e. anything being
// rendered by FullPageEditorLoader. At the current stage we skip server-rendering of content
// area so the component is empty.
export const EditPageLoaderServer = ({
	contentId,
	spaceKey,
	draftShareId,
	contentType,
}: EditPageLoaderServerProps) => {
	const [isUnpublishedDraft, setIsUnpublishedDraft] = useState(false);

	const livePageSSRStatus = useIsLivePageWithSSR();
	if (livePageSSRStatus.isTypeKnown && livePageSSRStatus.isLive) {
		// Remove draftShareId if it exists, to avoid persisting it on live doc. If the param exists on live doc, it prevents SSR from preloading live doc title/content
		return (
			<Redirection
				name={VIEW_PAGE.name}
				params={{ contentId, spaceKey }}
				excludedQueryParams={['draftShareId']}
			/>
		);
	}

	void (async () => {
		const queryResult = await queryInterop<NativeCollabQueryResponse>({
			query: NativeCollabQuery,
			variables: {
				contentId,
				status: ['draft', 'current'],
				...(draftShareId && { draftShareId }),
			},
			fetchPolicy: 'cache-first',
			context: { single: 'true' },
		});
		const newUnpublishedDraft = !queryResult.data?.content?.nodes?.[0]?.history?.createdBy;
		if (newUnpublishedDraft !== isUnpublishedDraft) {
			setIsUnpublishedDraft(newUnpublishedDraft);
		}
	})();

	return (
		<FullCycleRenderWithProfiler name="edit-page">
			<TreeRootForServerPlaceholderWrapper>
				<NativeCollabPresenceStateContainer scope={contentId}>
					<FullPageEditorLoader
						isUnpublishedDraft={isUnpublishedDraft}
						contentId={contentId}
						spaceKey={spaceKey}
						draftShareId={draftShareId}
						contentType={contentType}
						collabEditState={{}}
						heartbeatData={{}}
						disabled
					/>
				</NativeCollabPresenceStateContainer>
			</TreeRootForServerPlaceholderWrapper>
		</FullCycleRenderWithProfiler>
	);
};
